import { getRequest, postRequest } from "../../../utils/requests";
import config from "../../../config.json";

const BASE = (process.env.REACT_APP_API_BASE || `${config.service}/api`).replace(/\/$/, "");

/**
 * Функция для синхронизации звонков за определенный период
 * @param {string} startDate - Дата начала в формате YYYY-MM-DD
 * @param {string} endDate - Дата окончания в формате YYYY-MM-DD
 * @param {string} type - Тип звонков: '0'-Все, '1'-Входящий, '2'-Исходящий
 * @returns {Promise<Object>} Результат синхронизации
 */
export async function syncCalls(startDate, endDate, type = '0') {
  try {
    const response = await postRequest(`${BASE}/sipuni/sync`, {
      from: formatDateForSipuni(startDate),
      to: formatDateForSipuni(endDate),
      type: type,
      state: '0', // 0-Все, 1-Пропущенный, 2-Принятый
      timeFrom: '00:00',
      timeTo: '23:59'
    });

    return response;
  } catch (error) {
    console.error('Ошибка синхронизации звонков:', error);
    throw error;
  }
}

/**
 * Получение списка звонков с фильтрацией
 * @param {Object} filters - Фильтры для поиска
 * @returns {Promise<Object>} Список звонков с пагинацией
 */
export async function getCalls(filters = {}) {
  try {
    const params = new URLSearchParams({
      page: filters.page || 1,
      limit: filters.limit || 50,
      sortBy: filters.sortBy || 'date',
      sortOrder: filters.sortOrder || 'desc',
      ...filters
    });

    const { data } = await getRequest(`${BASE}/sipuni/calls?${params.toString()}`);
    return data;
  } catch (error) {
    console.error('Ошибка получения звонков:', error);
    throw error;
  }
}

/**
 * Получение статистики звонков
 * @param {string} startDate - Дата начала
 * @param {string} endDate - Дата окончания
 * @param {string} groupBy - Группировка: 'day', 'week', 'month'
 * @returns {Promise<Object>} Статистика звонков
 */
export async function getCallStatistics(startDate, endDate, groupBy = 'day') {
  try {
    const params = new URLSearchParams({
      startDate,
      endDate,
      groupBy
    });

    const { data } = await getRequest(`${BASE}/sipuni/statistics?${params.toString()}`);
    return data;
  } catch (error) {
    console.error('Ошибка получения статистики звонков:', error);
    throw error;
  }
}

/**
 * Получение статистики звонков по менеджерам для KPI
 * @param {string} from - Дата начала в ISO формате
 * @param {string} to - Дата окончания в ISO формате
 * @returns {Promise<Object>} Статистика по менеджерам
 */
export async function getManagersCallsStatistics(from, to, opts = {}) {
  try {
    const { period, normalize } = opts || {};
    const params = new URLSearchParams();
    if (period) {
      params.append('period', period);
    } else {
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (normalize) params.append('normalize', 'true');
    }

    const response = await getRequest(`${BASE}/sipuni/managers-stats?${params.toString()}`);
    
    console.log('📞 Ответ от Sipuni API:', response);
    
    // Реальный формат ответа от бэкенда:
    // {
    //   success: true,
    //   data: {
    //     managers: [
    //       {
    //         id: "6517c68cc239de81fa7f6d19",
    //         name: "Мусин Алибек",
    //         sipuniExtension: "206",
    //         calls: {
    //           totalCalls: 615,
    //           incomingCalls: 0,
    //           outgoingCalls: 447,
    //           totalMinutes: 677,
    //           incomingMinutes: 0,
    //           outgoingMinutes: 492
    //         }
    //       }
    //     ]
    //   }
    // }
    
    // Проверяем различные структуры ответа
    let managersData = [];
    
    if (response?.data?.data?.managers) {
      // Формат: { data: { data: { managers: [...] } } }
      managersData = response.data.data.managers;
      console.log('✅ Формат response.data.data.managers, найдено менеджеров:', managersData.length);
    } else if (response?.data?.managers) {
      // Формат: { data: { managers: [...] } }
      managersData = response.data.managers;
      console.log('✅ Формат response.data.managers, найдено менеджеров:', managersData.length);
    } else if (response?.managers) {
      // Формат: { managers: [...] }
      managersData = response.managers;
      console.log('✅ Формат response.managers, найдено менеджеров:', managersData.length);
    } else {
      console.warn('⚠️ Неожиданная структура ответа от /sipuni/managers-stats:', response);
      return { managers: [] };
    }
    
    // Проверяем что это массив
    if (!Array.isArray(managersData)) {
      console.warn('⚠️ managersData не является массивом:', managersData);
      return { managers: [] };
    }
    
    // Преобразуем данные в нужный формат
    const managers = managersData.map(manager => {
      const calls = manager.calls || {};
      
      return {
        id: manager.id,
        name: manager.name,
        sipuniExtension: manager.sipuniExtension,
        // Маппим поля из структуры calls
        callsIncoming: calls.incomingCalls || 0,
        callsOutgoing: calls.outgoingCalls || 0,
        callsTotal: calls.totalCalls || 0,
        callsAnswered: calls.answeredCalls || 0,
        callsMissed: calls.missedCalls || 0,
        totalMinutes: calls.totalMinutes || 0,
        incomingMinutes: calls.incomingMinutes || 0,
        outgoingMinutes: calls.outgoingMinutes || 0,
        averageDuration: calls.averageMinutes || 0
      };
    });
    
    console.log('📊 Обработано менеджеров с данными Sipuni:', managers.length);
    console.log('📞 Данные менеджеров:', managers.map(m => ({
      name: m.name,
      extension: m.sipuniExtension,
      incoming: m.callsIncoming,
      outgoing: m.callsOutgoing,
      total: m.callsTotal
    })));
    
    return { managers };
  } catch (error) {
    console.error('❌ Ошибка получения статистики звонков по менеджерам:', error);
    // Возвращаем пустые данные в случае ошибки
    return { managers: [] };
  }
}

/**
 * Получение истории звонков по номеру
 * @param {string} phoneNumber - Номер телефона
 * @param {number} limit - Лимит записей
 * @returns {Promise<Object>} История звонков
 */
export async function getCallHistory(phoneNumber, limit = 50) {
  try {
    const { data } = await getRequest(`${BASE}/sipuni/history/${phoneNumber}?limit=${limit}`);
    return data;
  } catch (error) {
    console.error('Ошибка получения истории звонков:', error);
    throw error;
  }
}

/**
 * Скачивание записи звонка
 * @param {string} callId - ID звонка
 * @returns {Promise<Blob>} Файл записи
 */
export async function downloadCallRecord(callId) {
  try {
    const response = await fetch(`${BASE}/sipuni/calls/${callId}/record`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    return await response.blob();
  } catch (error) {
    console.error('Ошибка скачивания записи звонка:', error);
    throw error;
  }
}

/**
 * Экспорт звонков в CSV
 * @param {Object} filters - Фильтры для экспорта
 * @returns {Promise<Blob>} CSV файл
 */
export async function exportCallsToCSV(filters = {}) {
  try {
    const params = new URLSearchParams(filters);
    
    const response = await fetch(`${BASE}/sipuni/export?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    return await response.blob();
  } catch (error) {
    console.error('Ошибка экспорта звонков:', error);
    throw error;
  }
}

/**
 * Вспомогательная функция для форматирования даты в формат Sipuni
 * @param {string|Date} date - Дата
 * @returns {string} Дата в формате dd.mm.yyyy
 */
function formatDateForSipuni(date) {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Класс для автоматической синхронизации звонков
 */
export class SipuniAutoSync {
  constructor(intervalMinutes = 60) {
    this.intervalMinutes = intervalMinutes;
    this.intervalId = null;
    this.isRunning = false;
    this.onSyncComplete = null;
    this.onSyncError = null;
  }

  /**
   * Запуск автоматической синхронизации
   */
  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    console.log(`Запуск автоматической синхронизации звонков каждые ${this.intervalMinutes} минут`);
    
    // Первая синхронизация
    this.syncNow();
    
    // Периодическая синхронизация
    this.intervalId = setInterval(() => {
      this.syncNow();
    }, this.intervalMinutes * 60 * 1000);
  }

  /**
   * Остановка автоматической синхронизации
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    console.log('Автоматическая синхронизация звонков остановлена');
  }

  /**
   * Немедленная синхронизация
   */
  async syncNow() {
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      const result = await syncCalls(todayStr, todayStr);
      console.log('Автоматическая синхронизация звонков выполнена:', result);
      
      if (this.onSyncComplete) {
        this.onSyncComplete(result);
      }
    } catch (error) {
      console.error('Ошибка автоматической синхронизации звонков:', error);
      
      if (this.onSyncError) {
        this.onSyncError(error);
      }
    }
  }
}

/**
 * Обработка ошибок API
 * @param {Error} error - Ошибка
 */
export function handleSipuniApiError(error) {
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 401:
        console.error('Sipuni API: Неавторизованный доступ');
        break;
      case 403:
        console.error('Sipuni API: Недостаточно прав');
        break;
      case 404:
        console.error('Sipuni API: Ресурс не найден');
        break;
      case 429:
        console.error('Sipuni API: Превышен лимит запросов');
        break;
      case 500:
        console.error('Sipuni API: Внутренняя ошибка сервера');
        break;
      default:
        console.error('Sipuni API:', data.message || 'Неизвестная ошибка');
    }
  } else if (error.request) {
    console.error('Sipuni API: Ошибка сети');
  } else {
    console.error('Sipuni API:', error.message || 'Произошла неизвестная ошибка');
  }
}
